import { useEffect, useMemo, useState } from "react";
import { http, inr, fmtErr } from "@/lib/api";
import { toast } from "sonner";

function useActiveStays(open) {
  const [stays, setStays] = useState([]);
  useEffect(() => {
    if (open) http.get("/admin/active-stay-options").then((r) => setStays(r.data.stays || [])).catch((e) => toast.error(fmtErr(e)));
  }, [open]);
  return stays;
}

function StayPicker({ stays, value, onChange }) {
  return (
    <div>
      <label className="text-xs block mb-1" style={{ color: "var(--muted)" }}>Guest / Active Stay</label>
      <select data-testid="entry-stay" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select guest / active stay…</option>
        {stays.map((s) => (
          <option key={s.id} value={s.id}>
            {s.guest_name}{s.booking_reference ? ` · ${s.booking_reference}` : ""} · {s.check_in_date} → {s.check_out_date}{s.responsible_manager ? ` · RM: ${s.responsible_manager}` : ""}
          </option>
        ))}
      </select>
      {stays.length === 0 && <p className="text-xs mt-1" style={{ color: "var(--terracotta)" }}>No active checked-in stays available to you.</p>}
    </div>
  );
}

function DialogShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h3 className="font-serif text-2xl mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

export function AddFoodOrderDialog({ onClose, onCreated }) {
  const stays = useActiveStays(true);
  const [stayId, setStayId] = useState("");
  const [cats, setCats] = useState([]);
  const [taxPct, setTaxPct] = useState(0);
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/food-items").then((r) => setCats(r.data.categories || [])).catch(() => {});
    http.get("/admin/settings/food-tax").then((r) => setTaxPct(r.data.enabled ? (r.data.percentage || 0) : 0)).catch(() => {});
  }, []);

  const items = useMemo(() => cats.flatMap((c) => (c.items || []).filter((it) => it.available)), [cats]);
  const subtotal = items.reduce((sum, it) => sum + (qty[it.id] || 0) * it.price, 0);
  const total = subtotal * (1 + taxPct / 100);
  const chosen = Object.entries(qty).filter(([, q]) => q > 0).length;

  const submit = async () => {
    if (!stayId) return toast.error("Select the guest's active stay");
    if (!chosen) return toast.error("Select at least one item with a quantity");
    setBusy(true);
    try {
      const payload = {
        items: Object.entries(qty).filter(([, q]) => q > 0).map(([food_item_id, q]) => ({ food_item_id, quantity: q })),
        special_instruction: notes || null,
        mark_acknowledged: ack,
      };
      const r = await http.post(`/admin/stays/${stayId}/food-orders`, payload);
      toast.success(`Order ${r.data.order_number} created — ${inr(r.data.total)} added to the guest's bill`);
      onCreated && onCreated();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <DialogShell title="Add Food Order" onClose={onClose}>
      <div className="space-y-3" data-testid="add-food-order-dialog">
        <StayPicker stays={stays} value={stayId} onChange={setStayId} />
        <div className="max-h-56 overflow-y-auto border rounded p-2 space-y-1" style={{ borderColor: "var(--border)" }}>
          {items.map((it) => (
            <div key={it.id} className="flex items-center gap-2 text-sm" data-testid={`entry-item-row-${it.id}`}>
              <span className="flex-1">{it.name} <span className="text-xs" style={{ color: "var(--muted)" }}>{inr(it.price)}</span></span>
              <input data-testid={`entry-item-qty-${it.id}`} type="number" min="0" max="50" value={qty[it.id] || ""}
                placeholder="0" onChange={(e) => setQty((p) => ({ ...p, [it.id]: Math.max(0, parseInt(e.target.value) || 0) }))}
                style={{ width: 64, padding: "4px 8px" }} />
            </div>
          ))}
          {items.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No available menu items.</p>}
        </div>
        <textarea data-testid="entry-notes" rows="2" placeholder="Special instructions / notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" style={{ width: "auto" }} data-testid="entry-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} />
          Mark as Acknowledged (I'm processing it immediately)
        </label>
        <div className="flex justify-between items-center text-sm pt-2 border-t" style={{ borderColor: "var(--border)" }}>
          <span style={{ color: "var(--muted)" }}>Total{taxPct ? ` (incl. ${taxPct}% food tax)` : " (no food tax)"}</span>
          <span className="font-serif text-xl" data-testid="entry-total">{inr(Math.round(total * 100) / 100)}</span>
        </div>
        <div className="flex gap-3">
          <button data-testid="entry-submit" onClick={submit} disabled={busy || !stayId || !chosen} className="btn-primary flex-1">{busy ? "Submitting…" : "Submit Order"}</button>
          <button data-testid="entry-cancel" onClick={onClose} className="btn-outline flex-1">Cancel</button>
        </div>
      </div>
    </DialogShell>
  );
}

export function AddServiceRequestDialog({ onClose, onCreated }) {
  const stays = useActiveStays(true);
  const [stayId, setStayId] = useState("");
  const [services, setServices] = useState([]);
  const [svcId, setSvcId] = useState("");
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/services").then((r) => setServices(r.data.services || [])).catch(() => {});
  }, []);

  const available = services.filter((s) => s.available && s.active !== false);
  const svc = available.find((s) => s.id === svcId);
  const total = svc ? svc.price * qty * (1 + (svc.tax_rate || 0) / 100) : 0;

  const submit = async () => {
    if (!stayId) return toast.error("Select the guest's active stay");
    if (!svcId) return toast.error("Select a service");
    setBusy(true);
    try {
      const r = await http.post(`/admin/stays/${stayId}/service-requests`, {
        service_id: svcId, quantity: qty, special_instruction: notes || null,
      });
      toast.success(`${r.data.service_name_snapshot} request created — ${inr(r.data.total)} added to the guest's bill`);
      onCreated && onCreated();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <DialogShell title="Add Service Request" onClose={onClose}>
      <div className="space-y-3" data-testid="add-service-request-dialog">
        <StayPicker stays={stays} value={stayId} onChange={setStayId} />
        <div>
          <label className="text-xs block mb-1" style={{ color: "var(--muted)" }}>Service</label>
          <select data-testid="entry-svc" value={svcId} onChange={(e) => setSvcId(e.target.value)}>
            <option value="">Select service…</option>
            {available.map((s) => <option key={s.id} value={s.id}>{s.name} · {inr(s.price)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: "var(--muted)" }}>Quantity</label>
          <input data-testid="entry-svc-qty" type="number" min="1" max="20" value={qty} onChange={(e) => setQty(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))} style={{ width: 90 }} />
        </div>
        <textarea data-testid="entry-notes" rows="2" placeholder="Notes / instructions (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <div className="flex justify-between items-center text-sm pt-2 border-t" style={{ borderColor: "var(--border)" }}>
          <span style={{ color: "var(--muted)" }}>Total</span>
          <span className="font-serif text-xl" data-testid="entry-total">{inr(Math.round(total * 100) / 100)}</span>
        </div>
        <div className="flex gap-3">
          <button data-testid="entry-submit" onClick={submit} disabled={busy || !stayId || !svcId} className="btn-primary flex-1">{busy ? "Submitting…" : "Submit"}</button>
          <button data-testid="entry-cancel" onClick={onClose} className="btn-outline flex-1">Cancel</button>
        </div>
      </div>
    </DialogShell>
  );
}
